import { createModel } from '@rematch/core';
import { cloneDeep, isEmpty, isUndefined } from 'lodash-es';
import type { RootModel } from 'models';
import { baseReducers, BaseState } from 'models/baseReducers';
import svcService from 'services/svcService';
import {
  BaseSvcCreateParams,
  Svc,
  SvcFilter,
  SvcSpec,
  SvcSpecCreateParams,
  SvcSpecUpdateParams,
  SvcType,
  SvcUpdateParams,
} from './../domains/svcDomain';

interface SVCState extends BaseState {
  data?: Svc[];
  pageSize: number;
  currentPage: number;
  viewingSvc: boolean;
  viewingSvcSpec: boolean;
  creatingSvc: boolean;
  creatingSvcSpec: boolean;
  curItem?: Partial<Svc>;
  curSpec?: Partial<SvcSpec>;
  svcTypes: SvcType[];
  baseSelectState: SVCBaseSelectState;
  specBaseSelectState: SVCSpecBaseSelectState;
  noMarkupCountSvc: number;
  noMarkupCountSvcmat: number;
  filterObj: SvcFilter;
}

type SVCBaseSelectState = {
  visible: boolean;
  curItem?: Svc;
  data?: Svc[];
};

type SVCSpecBaseSelectState = {
  visible: boolean;
  orgSvc?: Svc;
};

export const svc = createModel<RootModel>()({
  state: {
    data: undefined,
    pageSize: 20,
    currentPage: 1,
    curItem: undefined,
    curSpec: undefined,
    svcTypes: [],
    viewingSvc: false,
    viewingSvcSpec: false,
    creatingSvc: false,
    creatingSvcSpec: false,
    baseSelectState: {
      visible: false,
      curItem: undefined,
      data: undefined,
    },
    specBaseSelectState: {
      visible: false,
      orgSvc: undefined,
    },
    noMarkupCountSvc: 0,
    noMarkupCountSvcmat: 0,
    filterObj: {},
  } as SVCState,
  reducers: {
    ...baseReducers<SVCState>(),
    updateBaseSelectItem: (state: SVCState, newItem: Svc | undefined) => {
      state.baseSelectState.curItem = newItem;
      return state;
    },
    updateBaseSelectVisible: (state: SVCState, visible: boolean) => {
      state.baseSelectState.visible = visible;
      return state;
    },
    setBaseData: (state: SVCState, data: Svc[]) => {
      state.baseSelectState.data = data;
      return state;
    },
    updateSpecBaseSelectVisible: (state: SVCState, visible: boolean) => {
      state.specBaseSelectState.visible = visible;
      return state;
    },
    setOrgSvc: (state: SVCState, orgSvc: Svc | undefined) => {
      state.specBaseSelectState.orgSvc = orgSvc;
      return state;
    },
    reset: (state: SVCState) => {
      state.data = undefined;
      state.filterObj = {};
      state.curItem = undefined;
      state.curSpec = undefined;
      state.viewingSvc = false;
      state.viewingSvcSpec = false;
      state.creatingSvc = false;
      state.creatingSvcSpec = false;
      state.baseSelectState = {
        visible: false,
        curItem: undefined,
        data: undefined,
      };
      state.specBaseSelectState = {
        visible: false,
        orgSvc: undefined,
      };
      return state;
    },
  },
  effects: (dispatch) => {
    const { svc } = dispatch;
    return {
      async getSVCTypes(payload, state): Promise<void> {
        if (isEmpty(state.svc.svcTypes)) {
          const types = await svcService.getSVCTypes();

          svc.setState({
            svcTypes: types,
          });
        }
      },
      async getAllSVCs(): Promise<Svc[]> {
        const data = await svcService.getAllSVCs();
        svc.setState({
          data,
          noMarkupCountSvc: countNoMarkupSvc(data),
          noMarkupCountSvcmat: countNoMarkupSvcmat(data),
        });
        return data;
      },

      async getAllBaseSVCs(): Promise<void> {
        const data = await svcService.getAllBaseSVCs();
        svc.setBaseData(data);
      },

      async createBaseSvc(payload: BaseSvcCreateParams, state): Promise<void> {
        const createdSvc = await svcService.createBaseSvc(payload);
        svc.setState({
          data: state.svc.data ? [...state.svc.data, createdSvc] : [createdSvc],
          noMarkupCountSvc: (state.svc.noMarkupCountSvc || 0) + 1, // newly created svc has zero specs, so no markups
          noMarkupCountSvcmat: (state.svc.noMarkupCountSvcmat || 0) + 1,
        });
      },

      async createOrgSvc(payload: number, state): Promise<void> {
        const createdSvc = await svcService.createOrgSvc(payload);
        svc.setState({
          data: state.svc.data ? [...state.svc.data, createdSvc] : [createdSvc],
          noMarkupCountSvc:
            (state.svc.noMarkupCountSvc || 0) +
            (createdSvc.svcSpecs.find((spec) => spec.markupPct === null)
              ? 1
              : 0),
          noMarkupCountSvcmat:
            (state.svc.noMarkupCountSvcmat || 0) +
            (createdSvc.svcSpecs.find((spec) => spec.materialMarkupPct === null)
              ? 1
              : 0),
        });
      },

      async updateSvc(payload: SvcUpdateParams, state): Promise<void> {
        const updatedSvc = await svcService.updateSVC(payload);
        const updatedIndex = state.svc.data!.findIndex(
          (svc) => svc.id === updatedSvc.id
        );
        if (updatedIndex >= 0) {
          const clonedSvcList = cloneDeep(state.svc.data);
          clonedSvcList![updatedIndex] = updatedSvc;
          svc.setState({
            data: clonedSvcList,
          });
        }
      },

      async deleteSVC(payload: number, state): Promise<void> {
        await svcService.deleteSVC(payload);
        const deletedIndex = state.svc.data!.findIndex(
          (svc) => svc.id === payload
        );
        const deletedSvc = state.svc.data![deletedIndex];
        if (deletedIndex >= 0) {
          const clonedSvcList = cloneDeep(state.svc.data);
          clonedSvcList!.splice(deletedIndex, 1);
          svc.setState({
            data: clonedSvcList,
            noMarkupCountSvc:
              (state.svc.noMarkupCountSvc || 0) -
              (deletedSvc.svcSpecs.find((spec) => spec.markupPct === null)
                ? 1
                : 0),
            noMarkupCountSvcmat:
              (state.svc.noMarkupCountSvcmat || 0) -
              (deletedSvc.svcSpecs.find(
                (spec) => spec.materialMarkupPct === null
              )
                ? 1
                : 0),
          });
        }
      },

      async createBaseSpec(payload: SvcSpecCreateParams, state): Promise<void> {
        const createdSpec = await svcService.createBaseSpec(payload);
        const updatedIndex = state.svc.data!.findIndex(
          (svc) => svc.id === createdSpec.svcId
        );
        if (updatedIndex >= 0) {
          const clonedSvcList = cloneDeep(state.svc.data);
          const updatedSvc = clonedSvcList![updatedIndex];
          if (updatedSvc.svcSpecs === null) updatedSvc.svcSpecs = [];
          updatedSvc.svcSpecs.push(createdSpec);
          svc.setState({
            data: clonedSvcList,
            noMarkupCountSvc: countNoMarkupSvc(clonedSvcList!),
            noMarkupCountSvcmat: countNoMarkupSvcmat(clonedSvcList!),
          });
        }
      },

      async updateSpec(payload: SvcSpecUpdateParams, state): Promise<void> {
        const updatedSpec = await svcService.updateSpec(payload);
        const updatedSvcIndex = state.svc.data!.findIndex(
          (svc) => svc.id === updatedSpec.svcId
        );
        if (updatedSvcIndex >= 0) {
          const clonedSvcList = cloneDeep(state.svc.data);
          const updatedSvc = clonedSvcList![updatedSvcIndex];
          const updatedSpecIndex = updatedSvc.svcSpecs.findIndex(
            (spec) => spec.id === updatedSpec.id
          );
          if (updatedSpecIndex >= 0) {
            updatedSvc.svcSpecs[updatedSpecIndex] = updatedSpec;
            svc.setState({
              data: clonedSvcList,
              noMarkupCountSvc: countNoMarkupSvc(clonedSvcList!),
              noMarkupCountSvcmat: countNoMarkupSvcmat(clonedSvcList!),
            });
          }
        }
      },

      async addSpec(
        payload: { baseSpecId: number; orgSvcId: number },
        state
      ): Promise<void> {
        const addedSpec = await svcService.addSpec(
          payload.baseSpecId,
          payload.orgSvcId
        );
        const updatedIndex = state.svc.data!.findIndex(
          (svc) => svc.id === addedSpec.svcId
        );
        if (updatedIndex >= 0) {
          const clonedSvcList = cloneDeep(state.svc.data);
          const updatedSvc = clonedSvcList![updatedIndex];
          updatedSvc.svcSpecs.push(addedSpec);
          svc.setState({
            data: clonedSvcList,
            noMarkupCountSvc: countNoMarkupSvc(clonedSvcList!),
            noMarkupCountSvcmat: countNoMarkupSvcmat(clonedSvcList!),
          });
        }
      },

      async deleteSpec(
        payload: { svcId: number; specId: number },
        state
      ): Promise<void> {
        await svcService.deleteSpec(payload.specId);
        const updatedSvcIndex = state.svc.data!.findIndex(
          (svc) => svc.id === payload.svcId
        );
        if (updatedSvcIndex >= 0) {
          const clonedSvcList = cloneDeep(state.svc.data);
          const updatedSvc = clonedSvcList![updatedSvcIndex];
          const deletedSpecIndex = updatedSvc.svcSpecs.findIndex(
            (spec) => spec.id === payload.specId
          );
          if (deletedSpecIndex >= 0) {
            updatedSvc.svcSpecs.splice(deletedSpecIndex, 1);
            svc.setState({
              data: clonedSvcList,
              noMarkupCountSvc: countNoMarkupSvc(clonedSvcList!),
              noMarkupCountSvcmat: countNoMarkupSvcmat(clonedSvcList!),
            });
          }
        }
      },

      async saveFilter(payload: SvcFilter): Promise<void> {
        svc.setState({
          currentPage: 1,
          filterObj: payload,
        });
      },

      async removeFilter(): Promise<void> {
        svc.clearFilter();
      },
    };
  },
});

function countNoMarkupSvc(data: Svc[]): number {
  if (isUndefined(data) || isEmpty(data)) return 0;
  return data
    .map(
      (svc) =>
        (svc.svcSpecs.find((spec) => spec.markupPct === null) ? 1 : 0) as number
    )
    .reduce((prev, curr) => prev + curr, 0);
}

function countNoMarkupSvcmat(data: Svc[]): number {
  if (isUndefined(data) || isEmpty(data)) return 0;
  return data
    .map(
      (svc) =>
        (svc.svcSpecs.find((spec) => spec.materialMarkupPct === null)
          ? 1
          : 0) as number
    )
    .reduce((prev, curr) => prev + curr, 0);
}
